import { createRouter, createWebHistory } from 'vue-router';
import store from '@/store';
import { logAction } from '@/utils/logger.js';
import { AuthService } from '@/utils/auth.js'
import { ElMessage } from 'element-plus'
import axios from 'axios'

const routes = [
  {
    path: '/',
    name: 'home',
    component: () => import('@/views/Home.vue'),
    meta: { parent: null }
  },
  {
    path: "/characters",
    name: 'character',
    component: () => import('@/views/character/CharacterView.vue'),
    meta: { parent: '/' }
  },
  {
    path: '/epigraphs/:id',
    name: 'EpigraphView',
    component: () => import('@/views/epigraph/EpigraphView.vue'),
    meta: { checkAccess: true, parent: '/epigraphs/recommend' }
  },
  {
    path: '/epigraphs/detail/:uuid',
    name: 'EpigraphDetail',
    component: () => import('@/views/epigraph/EpigraphDetail.vue'),
    meta: { parent: null }
  },
  {
    path: '/epigraphs/recommend',
    name: 'EpigraphRecommend',
    component: () => import('@/views/epigraph/EpigraphRecommend.vue'),
    meta: { parent: '/' }
  },
  {
    path: '/characters/detail',
    name: 'CharacterDetail',
    component: () => import('@/views/character/CharacterDetail.vue'),
    meta: { parent: '/characters' }
  },
  {
    path: '/books',
    name: 'BookView',
    component: () => import('@/views/book/BookView.vue'),
    meta: { requiresAuth: true, parent: '/books/recommend' }
  },
  {
    path: '/books/:uuid',
    name: 'BookDetail',
    component: () => import('@/views/book/BookDetail.vue'),
    meta: { parent: '/books' }
  },
  {
    path: '/books/recommend',
    name: 'BookRecommend',
    component: () => import('@/views/book/BookRecommend.vue'),
    meta: { parent: '/' }
  },
  {
    path: '/tools',
    name: 'OnlineTools',
    component: () => import('@/views/OnlineTools.vue'),
    meta: { parent: '/' }
  },
  {
    path: '/user',
    name: 'UserCenter',
    component: () => import('@/views/UserCenter.vue'),
    meta: { requiresAuth: true, parent: '/' }
  },
  {
    path: '/auth/callback',
    name: 'OAuthCallback',
    component: () => import('@/views/OAuthCallback.vue'),
    meta: { parent: '/' }
  },
  {
    path: '/platform',
    name: 'Platform',
    component: () => import('@/views/management/Platform.vue'),
    meta: { requiresAuth: true, parent: '/' }
  },
  {
    path: '/proofread',
    name: 'Proofread',
    component: () => import('@/views/edit/Proofread.vue'),
    meta: { parent: '/platform' }
  },
  {
    path: '/audit',
    name: 'Audit',
    component: () => import('@/views/edit/Audit.vue'),
    meta: { parent: '/platform' }
  },
  {
    path: '/audit/review/:id',
    name: 'AuditReview',
    component: () => import('@/views/edit/AuditReview.vue'),
    meta: { requiresAuth: true, parent: '/platform' }
  },
  {
    path: '/results/epigraph/:uuid',
    name: 'EpigraphResult',
    component: () => import('@/views/edit/EpigraphResult.vue'),
    meta: { requiresAuth: true, parent: '/platform' }
  },
  {
    path: '/agent',
    name: 'SearchAgent',
    component: () => import('@/views/SearchAgent.vue'),
    meta: { parent: '/' }
  },
  {
    path: '/about',
    name: 'About',
    component: () => import('@/views/About.vue'),
    meta: { parent: '/' }
  }
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

function getTargetTypeAndId(to) {
  let targetType = 'page';   // 默认类型
  let targetId = to.params.id || to.params.uuid || null;

  // epigraph 相关页面
  if (to.path.startsWith('/epigraphs')) {
    targetType = 'epigraph';
    // 如果是推荐页，没有 ID 就保留 null
  }

  // books 相关页面
  else if (to.path.startsWith('/books')) {
    targetType = 'book';
  }

  // characters 相关页面
  else if (to.path.startsWith('/characters')) {
    targetType = 'character';
  }

  // 其他页面，保持默认 'page'
  return { targetType, targetId };
}

router.beforeEach(async (to, from, next) => {
  const userStr = localStorage.getItem('user');
  const isLoggedInInStore = store.getters.isLoggedIn;

  if (!userStr && isLoggedInInStore) {
    console.warn('Vuex状态与LocalStorage不一致，清理...');
    store.commit('LOGOUT');
  }

  // 处理需要登录的页面
  if (to.meta.requiresAuth) {
    const status = AuthService.checkLoginStatus(store);

    if (!status.isValid) {
      store.commit('LOGOUT');
      AuthService.clearLoginData();

      ElMessage.warning(
        status.reason === 'expired' ? '登录已过期，请重新登录' : '请先登录后再访问'
      );
      return next('/');
    }
  }

  // 处理专题访问权限检查
  if (to.meta.checkAccess && to.name === 'EpigraphView') {
    const topicId = to.params.id;
    const status = AuthService.checkLoginStatus();

    // 如果是游客，直接检查topicId是否为示例专题的ID
    if (status.isGuest) {
      // 如果不是示例专题，直接拦截
      if (topicId !== '13') {
        ElMessage.warning('请先注册/登录后访问');
        return next('/epigraphs/recommend');
      }
      // 如果是示例专题，允许通过
      return next();
    }

    try {
      // 查询专题信息（只对已登录用户）
      const topicRes = await axios.get(`/api/epigraph/${topicId}`);
      const topic = topicRes.data.data;

      // 系统管理员可以访问所有专题
      if (AuthService.isSystemAdmin(status.user)) {
        return next();
      }

      // 普通用户：检查是否为公开专题或属于用户的团队
      if (topic.isPublic) {
        return next();
      }

      // 检查用户是否属于该专题的团队
      const teamsRes = await axios.get('/api/my-teams');
      const userTeamIds = (teamsRes.data.teams || []).map(t => String(t.id));

      if (userTeamIds.includes(String(topic.teamId))) {
        return next();
      }

      // 没有权限
      ElMessage.warning('您没有访问此专题的权限');
      return next('/epigraphs/recommend');

    } catch (error) {
      console.error('检查专题权限失败:', error);
      ElMessage.error('加载专题信息失败');
      return next('/epigraphs/recommend');
    }
  }

  next();
});

router.afterEach((to, from) => {
  const { targetType, targetId } = getTargetTypeAndId(to);

  logAction({
    action: '访问页面',
    targetType,
    targetId,
    description: `访问页面: ${to.name}`,
    extra: { path: to.fullPath, from: from.fullPath }
  });

  console.log('当前用户 state:', store.state.user);
});

export default router
